/**
 * ErrorBoundary.tsx — top-level safety net.
 *
 * If a React render throws (bad data shape from the API, undefined access in
 * a chart, etc.) this catches the explosion and shows a recoverable error
 * card instead of a blank page.
 */

import React from 'react';

import { logger } from '../helpers/logger';

interface Props {
	children: React.ReactNode;
}

interface State {
	error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo): void {
		logger.error('Render error caught by boundary', error, info);
	}

	handleReset = (): void => {
		this.setState({ error: null });
	};

	render(): React.ReactNode {
		if (this.state.error) {
			return (
				<div
					style={{
						minHeight: '100vh',
						display: 'grid',
						placeItems: 'center',
						background: 'var(--bg)',
						padding: 24,
					}}
				>
					<div
						className="card"
						style={{ maxWidth: 520, padding: 28, textAlign: 'center' }}
						role="alert"
					>
						<h2
							style={{
								fontFamily: 'var(--font-display)',
								fontSize: 20,
								margin: '0 0 8px',
								color: 'var(--bad-600)',
							}}
						>
							Something went wrong.
						</h2>
						<p style={{ color: 'var(--text-dim)', fontSize: 13.5, margin: '0 0 16px' }}>
							The page hit an unexpected error. You can try again, or reload the app.
						</p>
						<pre
							style={{
								fontSize: 11,
								color: 'var(--text-faint)',
								background: 'var(--ink-50)',
								padding: 10,
								borderRadius: 8,
								textAlign: 'left',
								overflow: 'auto',
								maxHeight: 140,
							}}
						>
							{this.state.error.message}
						</pre>
						<div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
							<button type="button" className="btn ghost" onClick={this.handleReset}>
								Try again
							</button>
							<button
								type="button"
								className="btn"
								onClick={() => window.location.assign('/')}
							>
								Reload
							</button>
						</div>
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}
